/**
 * Feed content quality filter
 *
 * Filters out spam, low-quality content, lewd material, and memes from the
 * global feed to produce a more tech / open-source focused experience.
 *
 * This is pure client-side filtering applied AFTER relay events arrive.
 * Nothing is censored at the relay level — users can always disable this
 * in their settings. The goal is signal-over-noise for the default global view.
 */
import type { NostrEvent } from '@nostrify/nostrify';

// ── Known spam domains (link-farm / SEO bots) ────────────────────────────────
// These domains have been identified as operated by automated spam accounts
// that flood Nostr with keyword/hashtag dumps linking back to their own
// "semantic search" sites with no real user value.
const SPAM_DOMAINS = new Set([
  'allgraph.ro',
  'aepiot.ro',
  'aepiot.com',
  'headlines-world.com',
  'advanced-search.allgraph.ro',
  'advanced-search.aepiot.com',
  'advanced-search.aepiot.ro',
  'semantic-search.aepiot.ro',
  'semantic-search.headlines-world.com',
  'multi-search-tag-explorer.allgraph.ro',
]);

// ── Blocked hashtags (lewd, meme, spam) ─────────────────────────────────────
const BLOCKED_TAGS = new Set([
  // lewd / adult
  'nsfw', 'porn', 'nude', 'naked', 'xxx', 'onlyfans', 'adult', 'erotic',
  'sexy', 'lingerie', 'horny', 'hentai', 'lewd', '18plus', 'of', 'suggestive',
  // meme / shitpost noise
  'meme', 'memes', 'shitpost', 'shitposting', 'pepe', 'wojak', 'frog',
  'copypasta', 'greentext', 'ratio',
  // spam vectors
  'airdrop', 'giveaway', 'freemoney', 'getrich', 'investment',
  'forex', 'pumpanddump', 'rug', 'rugged', 'casino', 'gambling',
  'followback', 'followme', 'follow4follow', 'like4like',
]);

// ── Spam / low-quality keyword patterns ────────────────────────────────────
const SPAM_PATTERNS: RegExp[] = [
  /\bDM\s*for\s*(info|deals|price|rates|work)/i,
  /click\s*here\s*to\s*(earn|make|get)/i,
  /\b(earn|make)\s+\$?\d+\s*(per|\/)\s*(day|hour|week)/i,
  /join\s+my\s+(channel|group|telegram|whatsapp)/i,
  /\bfree\s+bitcoin\b/i,
  /\bpump\s+it\b|\bto\s+the\s+moon\b.*\b(buy|sell)\b/i,
  /\bsend\s+\d+\s*(btc|eth|sol)\b/i,
  /((🍑|🍆|👅|🔞){2,})/,
  /\bonly\s*fans\b/i,
  /(💰|🤑){3,}/,
  /follow\s*(me|back)\s*for\s*follow/i,
  /\b(whitelist|presale|ico|ido)\b.*\b(slot|spot|limited)\b/i,
  /\bairdrop\s*(claim|live|now|alert)\b/i,
];

// ── Lewd content heuristics ─────────────────────────────────────────────────
const LEWD_PATTERNS: RegExp[] = [
  /\b(nude|naked|topless|nudes)\b/i,
  /\b(porn|pornhub|xvideos|xhamster|redtube)\b/i,
  /\b(onlyfans\.com|fans\.ly)\b/i,
  /\b(hentai|loli|shota)\b/i,
  /(🍑|🍆|👅).*\b(dm|link|check|only)\b/i,
];

// ── URL extraction helper ─────────────────────────────────────────────────────
function extractDomains(content: string): string[] {
  const matches = content.match(/https?:\/\/([^/\s?#]+)/g) ?? [];
  return matches.map(m => {
    try { return new URL(m).hostname.toLowerCase(); } catch { return ''; }
  }).filter(Boolean);
}

// ── Check if a note has a content warning ───────────────────────────────────
function hasContentWarning(event: NostrEvent): boolean {
  return event.tags.some(tag => tag[0] === 'content-warning' || tag[0] === 'cw');
}

// ── Extract hashtags from event tags ────────────────────────────────────────
function getHashtags(event: NostrEvent): string[] {
  return event.tags.filter(t => t[0] === 't').map(t => t[1]?.toLowerCase() ?? '');
}

// ── Check if content is too short to be meaningful ──────────────────────────
function isTooShort(content: string): boolean {
  const stripped = content.replace(/https?:\/\/\S+/g, '').replace(/nostr:[a-z0-9]+/gi, '').trim();
  // Pure emoji strings or very short blurbs after removing URLs
  const emojiOnly = /^[\u{1F000}-\u{1FFFF}\u{2600}-\u{27FF}\s]+$/u.test(stripped);
  return emojiOnly && stripped.length < 30;
}

/**
 * SEO link-farm / hashtag-dump detector.
 *
 * This pattern is used by automated bots that post dozens of random #HASHTAGS
 * each immediately followed by a URL pointing to their own search engine site,
 * with no human-readable sentences between them. The aéPiot / allgraph.ro
 * account is a known example.
 *
 * Heuristics applied (any one match = spam):
 *  1. Content links to a known spam domain
 *  2. Very high hashtag count (>= 8 t-tags) with high link-to-text ratio
 *  3. Pattern: #WORD immediately concatenated with https:// (no space)
 *  4. Content is overwhelmingly links — almost no prose
 *  5. Repetitive ?lang=en&q= query-string pattern (search engine SEO dump)
 */
function isLinkFarmSpam(event: NostrEvent, tTagCount: number): boolean {
  const content = event.content ?? '';

  // 1. Any URL points to a known spam domain
  const domains = extractDomains(content);
  for (const domain of domains) {
    if (SPAM_DOMAINS.has(domain)) return true;
    // Also catch subdomains of known spam TLDs
    if (domain.endsWith('.allgraph.ro') || domain.endsWith('.aepiot.ro') ||
        domain.endsWith('.aepiot.com') || domain.endsWith('.headlines-world.com')) {
      return true;
    }
  }

  // 2. High t-tag count (bots typically tag 10-30 keywords per post)
  // Combined with a high URL density it's almost certainly automated
  if (tTagCount >= 8) {
    const urlCount = (content.match(/https?:\/\//g) ?? []).length;
    if (urlCount >= 5) return true; // 8+ tags AND 5+ URLs = bot dump
  }

  // 3. Hashtag immediately glued to a URL: "#FOOhttps://" with no space
  //    Human writers always put a space or newline; bots concatenate directly.
  if (/#[A-Z][A-Z\s]+https:\/\//i.test(content)) return true;

  // 4. Overwhelmingly links — strip all URLs and if < 20 real chars remain
  //    it's not a human note
  const stripped = content
    .replace(/https?:\/\/\S+/g, '')
    .replace(/#\w+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (content.length > 200 && stripped.length < 20) return true;

  // 5. SEO query-string dump: ?lang=en&q= appearing 3+ times
  const seoQueryMatches = content.match(/\?lang=[a-z]{2}&q=/g) ?? [];
  if (seoQueryMatches.length >= 3) return true;

  return false;
}

/** Main filter function — returns true if the event should be SHOWN */
export function isQualityContent(event: NostrEvent): boolean {
  // Always pass through reposts (kind 6, 16) — let the original speak
  if (event.kind === 6 || event.kind === 16) return true;
  // Always pass through long-form articles (kind 30023)
  if (event.kind === 30023) return true;

  const content = event.content ?? '';
  const tTags = getHashtags(event);

  // 1. Drop events with NIP-36 content-warning tags
  if (hasContentWarning(event)) return false;

  // 2. Drop events with blocked hashtags
  for (const tag of tTags) {
    if (BLOCKED_TAGS.has(tag)) return false;
  }

  // 3. Drop link-farm / SEO bot spam (catches the aéPiot-style accounts)
  if (isLinkFarmSpam(event, tTags.length)) return false;

  // 4. Drop lewd content based on keyword patterns
  for (const pattern of LEWD_PATTERNS) {
    if (pattern.test(content)) return false;
  }

  // 5. Drop obvious spam patterns
  for (const pattern of SPAM_PATTERNS) {
    if (pattern.test(content)) return false;
  }

  // 6. Drop pure emoji / very-short posts
  if (isTooShort(content)) return false;

  return true;
}

/** Filter mode — used by FeedPage to optionally apply filtering */
export type FeedFilterMode = 'all' | 'quality' | 'tech';

/**
 * Tech / open-source focused filter — layers on top of quality filter.
 * Keeps notes that mention tech topics or have tech hashtags.
 */
const TECH_TAGS = new Set([
  // Bitcoin & L2
  'bitcoin', 'btc', 'lightning', 'lnd', 'cln', 'lnbits', 'cashu', 'ecash',
  'liquid', 'sidechain', 'taproot', 'rgb', 'dlc',
  // Nostr
  'nostr', 'nip', 'ndk', 'nostrdev', 'nostricos', 'nostrify',
  // Open source / dev
  'opensource', 'foss', 'linux', 'ubuntu', 'debian', 'fedora', 'nixos',
  'programming', 'coding', 'developer', 'dev', 'devs', 'software',
  'python', 'rust', 'golang', 'typescript', 'javascript', 'cpp',
  'react', 'svelte', 'vue', 'angular', 'nextjs',
  'git', 'github', 'gitlab', 'selfhosted',
  // Privacy / security
  'privacy', 'security', 'infosec', 'cybersecurity', 'tor', 'vpn',
  'encryption', 'pgp', 'gpg',
  // AI / ML
  'ai', 'llm', 'machinelearning', 'ml', 'ollama', 'localai',
  // Hardware / makers
  'hardware', 'raspberrypi', 'arduino', 'esp32', 'diy',
  // Liberty / freedom tech
  'freedomtech', 'sovereigntech', 'decentralization', 'p2p',
]);

export function isTechContent(event: NostrEvent): boolean {
  if (!isQualityContent(event)) return false;
  if (event.kind === 30023) return true; // articles always pass

  const tags = getHashtags(event);
  for (const tag of tags) {
    if (TECH_TAGS.has(tag)) return true;
  }

  // Also check content for tech keywords (lightweight check)
  const content = event.content.toLowerCase();
  const techKeywords = [
    'bitcoin', 'nostr', 'lightning', 'open source', 'foss', 'linux',
    'github', 'rust lang', 'typescript', 'privacy', 'encryption',
    'self-host', 'selfhost', 'cashu', 'ecash', 'relay',
  ];
  for (const kw of techKeywords) {
    if (content.includes(kw)) return true;
  }

  return false;
}

/** Apply the selected filter mode to a list of events */
export function applyFeedFilter(events: NostrEvent[], mode: FeedFilterMode): NostrEvent[] {
  if (mode === 'all') return events;
  if (mode === 'quality') return events.filter(isQualityContent);
  if (mode === 'tech') return events.filter(isTechContent);
  return events;
}
